import { getApiUrl } from "../api";
import { useEffect, useMemo, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { isAuthenticated, setAuthReturnPath } from "../auth";
import { getCloudflareImageUrl } from "../utils/cloudflareImages";
import { ArabicFooter, ArabicHeader } from "./ArabicHome";


const stripHtml = (value) => String(value || "")
  .replace(/<br\s*\/?>/gi, "\n")
  .replace(/<\/p>/gi, "\n")
  .replace(/<[^>]*>/g, " ")
  .replace(/\s+/g, " ")
  .trim();

const splitContent = (value) => String(value || "")
  .replace(/<br\s*\/?>/gi, "\n")
  .replace(/<\/p>/gi, "\n")
  .replace(/<li[^>]*>/gi, "\n")
  .replace(/<[^>]*>/g, " ")
  .split(/\r?\n|[\u2022|]/)
  .map((item) => item.replace(/\s+/g, " ").trim())
  .filter(Boolean);

const mapDocument = (item, language = "en") => ({
  id: item.id || "",
  catid: item.catid || "",
  subcatid: item.subcatid || "",
  categoryTitle: stripHtml(language === "ar" ? (item.category_title_ar || item.category_title_en) : item.category_title_en) || "Required Documents",
  subcategoryTitle: stripHtml(language === "ar" ? (item.subcategory_title_ar || item.subcategory_title_en) : item.subcategory_title_en) || "",
  title: stripHtml(language === "ar" ? (item.title_ar || item.title_en || item.title) : (item.title_en || item.title)) || "",
  position: Number(item.position) || 0,
});

const mapContentItem = (item, language = "en") => ({
  id: item.id || "",
  catid: item.catid || "",
  subcatid: item.subcatid || "",
  title: stripHtml(language === "ar" ? (item.title_ar || item.title_en || item.title) : (item.title_en || item.title)) || "",
  position: Number(item.position) || 0,
});

const mapDocCategory = (item, language = "en") => ({
  id: item.id || "",
  title: stripHtml(language === "ar" ? (item.title_ar || item.title_en || item.title) : (item.title_en || item.title)) || "Required Documents",
  badge: stripHtml(language === "ar" ? (item.badgetitle_ar || item.badgetitle_en) : item.badgetitle_en) || "",
  icon: getCloudflareImageUrl(item.icon, "w=80,sharpen=1"),
  position: Number(item.position) || 0,
});

const getQueryValue = (params, key) => params.get(key) || "";
const getFirstValue = (...values) => values.find((value) => String(value || "").trim()) || "";
const getFirstItem = (data) => Array.isArray(data) ? data[0] : data;
const getJwtToken = () => {
  const keys = ["token", "accessToken", "jwt", "authToken"];
  for (const key of keys) {
    const token = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (token?.trim()) return token.replace(/^Bearer\s+/i, "").trim();
  }

  return (process.env.REACT_APP_JWT_TOKEN || "").replace(/^Bearer\s+/i, "").trim();
};

const getFetchOptions = (signal) => {
  const token = getJwtToken();
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  return {
    headers,
    credentials: "include",
    signal,
  };
};

const mergeServiceData = (primary, secondary) => {
  const merged = { ...(secondary || {}), ...(primary || {}) };

  Object.keys(secondary || {}).forEach((key) => {
    if (!String(merged[key] || "").trim() && String(secondary[key] || "").trim()) {
      merged[key] = secondary[key];
    }
  });

  return Object.keys(merged).length ? merged : null;
};

const documentCategoryIcons = [
  "fa-id-card",
  "fa-location-dot",
  "fa-wallet",
  "fa-briefcase",
  "fa-building",
  "fa-people-roof",
  "fa-landmark",
  "fa-folder-plus",
];

const CheckList = ({ items }) => {
  const list = items;

  return (
    <div className="sdn-check-list">
      {list.map((item) => (
        <div key={item}>
          <span>{"\u2713"}</span>
          <p>{item}</p>
        </div>
      ))}
    </div>
  );
};

const SectionHead = ({ icon, title, subtitle }) => (
  <div className="sdn-section-head">
    <div className="sdn-title-mark"><img src={icon} alt="" /></div>
    <div>
      <h2>{title}</h2>
      <p>{subtitle}</p>
    </div>
  </div>
);

export default function ServiceDetails({ language = "en" }) {
  const interactions = useHomeInteractions();
  const isArabic = language === "ar";
  const [activeTab, setActiveTab] = useState("overview");
  const [service, setService] = useState(null);
  const [docCategories, setDocCategories] = useState([]);
  const [requiredDocumentsByCategory, setRequiredDocumentsByCategory] = useState({});
  const [specificDocuments, setSpecificDocuments] = useState([]);
  const [serviceRequirements, setServiceRequirements] = useState([]);
  const [terms, setTerms] = useState([]);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const catid = getQueryValue(query, "catid");
  const subcatid = getQueryValue(query, "subcatid") || getQueryValue(query, "id");
  const id = getQueryValue(query, "id");

  useEffect(() => {
    document.title = isArabic ? "تفاصيل الخدمة | مؤسسة تراحم الخيرية" : "Service Details | Tarahum Charity Foundation";
  }, [isArabic]);

  useEffect(() => {
    if (!isArabic) return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => { document.body.classList.remove("arabic-layout-page"); document.documentElement.lang = "en"; document.documentElement.dir = "ltr"; };
  }, [isArabic]);

  const handleConfirm = (event) => {
    if (!accepted) {
      event.preventDefault();
      setActiveTab("terms");
      setError(isArabic ? "يرجى الموافقة على الشروط والأحكام قبل المتابعة." : "Please agree to the terms and conditions before continuing.");
      return;
    }

    setError("");
    if (!isAuthenticated()) {
      setAuthReturnPath(`/aid-application${window.location.search || ""}`);
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    const loadDetails = async () => {
      try {
        const detailParams = new URLSearchParams();
        if (catid) detailParams.set("catid", catid);
        if (subcatid) detailParams.set("subcatid", subcatid);
        if (id) detailParams.set("id", id);

        const detailResponse = await fetch(getApiUrl(`/api/GetDonationSubCategoryDetails.ashx?${detailParams.toString()}`), {
          ...getFetchOptions(controller.signal),
        });

        if (!detailResponse.ok) throw new Error(`Service details request failed with ${detailResponse.status}`);
        const detailResult = await detailResponse.json();
        const detail = getFirstItem(detailResult?.data) || null;

        const resolvedCatId = detail?.catid || catid;
        const resolvedSubCatId = detail?.id || subcatid || id;

        const documentRequests = [];
        let listDetail = null;

        if (resolvedCatId) {
          const listResponse = await fetch(getApiUrl(`/api/GetDonationSubCategoryList.ashx?catid=${encodeURIComponent(resolvedCatId)}&pageSize=100`), {
            ...getFetchOptions(controller.signal),
          });

          if (listResponse.ok) {
            const listResult = await listResponse.json();
            const listItems = Array.isArray(listResult?.data) ? listResult.data : [];
            listDetail = listItems.find((item) => String(item.id) === String(resolvedSubCatId || id)) || null;
          }
        }

        setService(mergeServiceData(detail, listDetail));

        const docCategoryResponse = await fetch(getApiUrl("/api/GetAidRequestDocCategoryListServiceDetails.ashx?pageSize=100"), getFetchOptions(controller.signal));
        let nextDocCategories = [];

        if (docCategoryResponse.ok) {
          const docCategoryResult = await docCategoryResponse.json();
          nextDocCategories = (Array.isArray(docCategoryResult?.data) ? docCategoryResult.data : [])
            .map((item) => mapDocCategory(item, language))
            .filter((category) => category.id);
          setDocCategories(nextDocCategories);
        }

        documentRequests.push(fetch(getApiUrl("/api/GetRequiredDocAllTypeList.ashx?pageSize=500"), getFetchOptions(controller.signal)));
        documentRequests.push(fetch(getApiUrl(`/api/GetAidReqServiceRequirementsList.ashx?catid=${encodeURIComponent(resolvedCatId || "")}&subcatid=${encodeURIComponent(resolvedSubCatId || "")}&pageSize=100`), getFetchOptions(controller.signal)));
        documentRequests.push(fetch(getApiUrl(`/api/GetAidReqTnCList.ashx?catid=${encodeURIComponent(resolvedCatId || "")}&subcatid=${encodeURIComponent(resolvedSubCatId || "")}&pageSize=100`), getFetchOptions(controller.signal)));

        if (resolvedCatId && resolvedSubCatId) {
          documentRequests.push(fetch(getApiUrl(`/api/GetRequiredDocByCategoryList.ashx?catid=${encodeURIComponent(resolvedCatId)}&subcatid=${encodeURIComponent(resolvedSubCatId)}&pageSize=100`), getFetchOptions(controller.signal)));
        }

        const [allDocsResponse, requirementsResponse, termsResponse, specificDocsResponse] = await Promise.all(documentRequests);

        if (allDocsResponse.ok) {
          const allDocsResult = await allDocsResponse.json();
          const allDocs = (Array.isArray(allDocsResult?.data) ? allDocsResult.data : []).map((item) => mapDocument(item, language)).filter((document) => document.title);
          const groupedDocs = nextDocCategories.reduce((groups, category) => {
            groups[category.id] = allDocs
              .filter((document) => String(document.catid) === String(category.id))
              .sort((left, right) => left.position - right.position);
            return groups;
          }, {});

          setRequiredDocumentsByCategory(groupedDocs);
        }

        if (requirementsResponse.ok) {
          const requirementsResult = await requirementsResponse.json();
          setServiceRequirements((Array.isArray(requirementsResult?.data) ? requirementsResult.data : []).map((item) => mapContentItem(item, language)).filter((item) => item.title));
        }

        if (termsResponse.ok) {
          const termsResult = await termsResponse.json();
          setTerms((Array.isArray(termsResult?.data) ? termsResult.data : []).map((item) => mapContentItem(item, language)).filter((item) => item.title));
        }

        if (specificDocsResponse && specificDocsResponse.ok) {
          const specificDocsResult = await specificDocsResponse.json();
          setSpecificDocuments((Array.isArray(specificDocsResult?.data) ? specificDocsResult.data : []).map((item) => mapDocument(item, language)).filter((document) => document.title));
        }

        setMessage(detail ? "" : (isArabic ? "تعذر العثور على تفاصيل الخدمة." : "Service details were not found."));
      } catch (error) {
        if (error.name !== "AbortError") {
          setMessage(isArabic ? "تعذر تحميل تفاصيل الخدمة حاليًا." : "Service details could not be loaded right now.");
          console.warn("Unable to load service details", error);
        }
      }
    };

    loadDetails();
    return () => controller.abort();
  }, [catid, id, isArabic, language, subcatid]);

  const title = stripHtml(isArabic ? (service?.title_ar || service?.title_en || service?.title) : (service?.title_en || service?.title));
  const categoryTitle = stripHtml(isArabic ? (service?.category_title_ar || service?.category_title_en) : service?.category_title_en);
  const description = stripHtml(isArabic ? (service?.aidrequestbannerdescription_ar || service?.aidrequestdescription_ar || service?.aidrequestbannerdescription_en) : (service?.aidrequestbannerdescription_en || service?.aidrequestdescription_en));
  const fullDescription = splitContent(isArabic ? (service?.aidrequestdescription_ar || service?.aidrequestbannerdescription_ar || service?.aidrequestdescription_en) : (service?.aidrequestdescription_en || service?.aidrequestbannerdescription_en));
  const bannerImage = getCloudflareImageUrl(getFirstValue(
    service?.aidrequestbanner,
    service?.AidRequestBanner,
    service?.aidRequestBanner,
    service?.ImagePath,
    service?.imagePath,
  ), "w=900,sharpen=1");
  const qrImage = getCloudflareImageUrl(getFirstValue(
    service?.aidrequestqrcode,
    service?.AidRequestQrCode,
    service?.AidRequestQRCode,
    service?.aidRequestQrCode,
    service?.aidRequestQRCode,
  ), "w=420,sharpen=1");
  const serviceDuration = stripHtml(isArabic ? (service?.aidrequestserviceduration_ar || service?.aidrequestserviceduration_en) : service?.aidrequestserviceduration_en);
  const targetAudience = stripHtml(isArabic ? (service?.aidrequesttargetaudiance_ar || service?.aidrequesttargetaudiance_en) : service?.aidrequesttargetaudiance_en);
  const emirates = stripHtml(isArabic ? (service?.aidrequestemirates_ar || service?.aidrequestemirates_en) : service?.aidrequestemirates_en);
  const tabs = isArabic ? [
    { id: "overview", label: "الوصف" }, { id: "requirements", label: "المتطلبات" },
    { id: "required-documents", label: "المستندات المطلوبة" }, { id: "assistance-documents", label: "مستندات خاصة بالمساعدة" },
    { id: "terms", label: "الشروط والأحكام" },
  ] : [
    { id: "overview", label: "Description" }, { id: "requirements", label: "Requirements" },
    { id: "required-documents", label: "Required Documents" }, { id: "assistance-documents", label: "Assistance Specific Documents" },
    { id: "terms", label: "Terms & Conditions" },
  ];

  return (
    <>
      <Preloader />
      {isArabic ? <ArabicHeader /> : <><Header /><LanguagePopup /></>}

      <section className="services-details-wrap-new">
        <div className="container">
          <div className="sdn-container">
            <div className="sdn-hero-modern">
              <div className="sdn-hero-left">
                <span className="sdn-pill">{categoryTitle}</span>
                <h1>{title}</h1>
                <p>{description}</p>

                <div className="sdn-action-row">
                  <a href="#overview" className="sdn-primary-btn" onClick={() => setActiveTab("overview")}>{isArabic ? "ابدأ الخدمة" : "Start Service"}</a>
                  <Link to="/about-us" className="sdn-secondary-btn">{isArabic ? "المزيد عنا" : "More about us"}</Link>
                </div>
              </div>

              <div className="sdn-hero-right">
                <div className="sad-hero-new-visual">
                  {bannerImage ? <img src={bannerImage} alt={title} /> : null}
                </div>
              </div>
            </div>

            {message ? (
              <div className="in-kind-alert" role="alert">
                <span className="in-kind-alert-icon"><i className="fa-solid fa-circle-info"></i></span>
                <span>
                  <strong>{isArabic ? "تنبيه المحتوى" : "Content notice"}</strong>
                  <small>{message}</small>
                </span>
              </div>
            ) : null}

            <h4 className="mb-20">{isArabic ? "معلومات الخدمة" : "Service Information"}</h4>

            <div className="sdn-summary-grid">
              <div className="sdn-summary-card">
                <div className="sdn-summary-icon"><img src="/images/img/hand.png" alt="" /></div>
                <div>
                  <span>{isArabic ? "مدة الخدمة" : "Service Duration"}</span>
                  <h3>{serviceDuration}</h3>
                </div>
              </div>

              <div className="sdn-summary-card">
                <div className="sdn-summary-icon"><img src="/images/img/target-group.png" alt="" /></div>
                <div>
                  <span>{isArabic ? "الفئة المستهدفة" : "Target Audience"}</span>
                  <h3>{targetAudience}</h3>
                </div>
              </div>

              <div className="sdn-summary-card">
                <div className="sdn-summary-icon"><img src="/images/img/location-1.png" alt="" /></div>
                <div>
                  <span>{isArabic ? "الإمارة" : "The Emirate"}</span>
                  <h3>{emirates}</h3>
                </div>
              </div>
            </div>

            <div className="sdn-main-layout">
              <div className="sdn-content-area">
                <div className="sad-wrap">
                  <div className="ne-tab-menu" id="neTabMenu">
                    {tabs.map((tab) => (
                      <button
                        className={`ne-tab-btn ${activeTab === tab.id ? "active" : ""}`}
                        type="button"
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  <div className={`ne-tab-content ${activeTab === "overview" ? "active" : ""}`} id="overview">
                    <div className="sdn-section-card">
                      <SectionHead icon="/images/img/discriptions.png" title={isArabic ? "الوصف" : "Description"} subtitle={isArabic ? "وصف الخدمة" : "Service Description"} />
                      <div className="sdn-modern-requirements">
                        {fullDescription.length ? fullDescription.map((item) => <p key={item}>{item}</p>) : <p>{description}</p>}
                      </div>
                    </div>
                  </div>

                  <div className={`ne-tab-content ${activeTab === "requirements" ? "active" : ""}`}>
                    <div className="sdn-section-card" id="requirements">
                      <SectionHead icon="/images/img/ret.png" title={isArabic ? "متطلبات الخدمة" : "Service Requirements"} subtitle={isArabic ? "المعلومات المطلوبة قبل البدء" : "Information needed before starting"} />
                      <div className="sdn-modern-requirements">
                        <CheckList items={serviceRequirements.map((item) => item.title)} />
                      </div>
                    </div>
                  </div>

                  <div className={`ne-tab-content ${activeTab === "required-documents" ? "active" : ""}`}>
                    <div className="sdn-section-card" id="required-documents">
                      <SectionHead icon="/images/img/requrmnt.png" title={isArabic ? "المستندات المطلوبة" : "Required Documents"} subtitle={isArabic ? "المعلومات المطلوبة قبل البدء" : "Information needed before starting"} />
                      <div className="sdn-modern-requirements">
                        <div className="aid-specific-grid aid-required-grid">
                          {docCategories.length ? docCategories.map((category, index) => {
                            const documents = requiredDocumentsByCategory[category.id] || [];
                            return (
                            <article className="aid-specific-card" key={category.id}>
                              <h4>
                                {category.icon ? <img src={category.icon} alt="" className="aid-specific-card-icon" /> : <i className={`fa-solid ${documentCategoryIcons[index % documentCategoryIcons.length]}`}></i>}
                                {category.title}
                              </h4>
                              <ul>
                                {documents.length ? documents.map((document) => <li key={document.id || document.title}>{document.title}</li>) : <li>No documents configured.</li>}
                              </ul>
                            </article>
                            );
                          }) : (
                            <article className="aid-specific-card">
                              <h4><i className="fa-solid fa-folder-plus"></i> Required Documents</h4>
                              <p>No general document list is available at the moment.</p>
                            </article>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={`ne-tab-content ${activeTab === "assistance-documents" ? "active" : ""}`}>
                    <div className="sdn-section-card" id="Assistance-Specific-Documents">
                      <SectionHead icon="/images/img/requrmnt.png" title={isArabic ? "مستندات خاصة بالمساعدة" : "Assistance Specific Documents"} subtitle={isArabic ? "اختر المستندات التي تناسب نوع المساعدة المطلوب في طلبك." : "Select the documents that match the assistance type requested in your application."} />
                      <div className="sdn-modern-requirements">
                        <h5 style={{ marginBottom: 0, lineHeight: 1.1 }}>{title}</h5>
                        <div className="sdn-check-list">
                          {specificDocuments.length ? specificDocuments.map((document) => (
                            <div key={document.id || document.title}>
                              <span>{"\u2713"}</span>
                              <p>{document.title}</p>
                            </div>
                          )) : (
                            <div>
                              <span>{"\u2713"}</span>
                              <p>No specific documents have been configured for this service.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={`ne-tab-content ${activeTab === "terms" ? "active" : ""}`}>
                    <div className="sdn-section-card" id="terms">
                      <SectionHead icon="/images/img/t-c.png" title={isArabic ? "الشروط والأحكام" : "Terms & Conditions"} subtitle={isArabic ? "أكد قبل المتابعة" : "Confirm before continuing"} />
                      <div className="sdn-modern-requirements">
                        <CheckList items={terms.map((item) => item.title)} />
                      </div>
                    </div>
                  </div>

                  <div className="sdn-section-card" style={{ marginTop: 30 }}>
                    <label className="sdn-modern-checkbox">
                      <input
                        type="checkbox"
                        checked={accepted}
                        onChange={(event) => {
                          setAccepted(event.target.checked);
                          if (event.target.checked) setError("");
                        }}
                      />
                      <span>{isArabic ? "أوافق على الشروط والأحكام الخاصة بهذه الخدمة." : "I agree to the terms and conditions of this service."}</span>
                    </label>

                    {error && (
                      <div className="quk-dnt" style={{ marginBottom: "15px" }}>
                        <div className="quick-donation-alert" role="alert">
                          <i className="fa-solid fa-circle-exclamation"></i>
                          {error}
                        </div>
                      </div>
                    )}

                    <Link
                      className="sdn-confirm-modern"
                      to={accepted
                        ? isAuthenticated()
                          ? `/aid-application${window.location.search || ""}`
                          : "/login"
                        : "#terms"}
                      onClick={handleConfirm}
                    >
                      {isArabic ? "تأكيد ومتابعة" : "Confirm & Continue"}
                    </Link>
                  </div>
                </div>
              </div>

              <aside className="sdn-sidebar-modern">
                <div className="sdn-side-panel">
                  {qrImage ? <img src={qrImage} alt="Service QR code" /> : null}
                </div>

                <div className="sdn-support-panel">
                  <span>{isArabic ? "هل تحتاج إلى مساعدة؟" : "Need Support?"}</span>
                  <h3>{isArabic ? "تواصل مع فريق الخدمة" : "Contact Service Team"}</h3>
                  <p>{isArabic ? "تواصل مع فريق دعم المؤسسة للحصول على المساعدة وإرشادات الخدمة." : "Reach the foundation support team for assistance and service guidance."}</p>

                  <div className="sdn-support-item">
                    <small>{isArabic ? "مركز الاتصال" : "Call Center"}</small>
                    <strong>042848000</strong>
                  </div>

                  <div className="sdn-support-item">
                    <small>{isArabic ? "البريد الإلكتروني" : "Email"}</small>
                    <strong>info@tarahum.ae</strong>
                  </div>

                  <Link to="/contact-us" className="sdn-support-btn">{isArabic ? "تواصل مع الدعم" : "Contact Support"}</Link>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </section>

      {isArabic ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
